import {
  CreateFirebaseToken,
  CreateUser,
  Service,
  UpdateUser,
  User,
  UserServicesRequest,
} from "@models";
import { userService } from "@services";
import i18n from "../i18n";
import { message } from "@messages";
import { makeAutoObservable, runInAction } from "mobx";

export default class UserStore {
  user: User | undefined = undefined;
  users = new Map<string, User>();
  loading = false;

  services = new Map<string, Service>();
  servicesLoading = false;
  servicesScopeKey: string | undefined = undefined;

  constructor() {
    makeAutoObservable(this);
  }

  getAllByTenantId = async (tenantId: string | undefined) => {
    this.setLoading(true);
    try {
      const response = await userService.getAllByTenantId(tenantId);
      runInAction(() => {
        response.forEach((user) => {
          this.users.set(user.id.toString(), user);
        });
        this.loading = false;
      });
    } catch (error) {
      console.error(error);
      this.setLoading(false);
    }
  };
  getServices = async (request: UserServicesRequest) => {
    const key = `${request.userId}|${request.tenantId}`;
    if (this.servicesScopeKey === key) return;
    this.setServicesLoading(true);
    runInAction(() => {
      this.services.clear();
      this.servicesScopeKey = key;
    });
    try {
      const services = await userService.getServices(request);
      runInAction(() => {
        if (this.servicesScopeKey !== key) return;
        services.forEach((service: Service) => {
          this.services.set(service.id.toString(), service);
        });
        this.servicesLoading = false;
      });
    } catch (error) {
      console.error(error);
      runInAction(() => {
        if (this.servicesScopeKey !== key) return;
        this.servicesScopeKey = undefined;
        this.servicesLoading = false;
      });
    }
  };

  get = async (id: number) => {
    this.setLoading(true);
    try {
      const response = await userService.get({ id });
      runInAction(() => {
        this.user = response;
        this.loading = false;
      });
    } catch (error) {
      console.error(error);
      this.setLoading(false);
    }
  };

  getProfile = async () => {
    this.setLoading(true);
    try {
      const response = await userService.getProfile();
      runInAction(() => {
        this.user = response;
        this.loading = false;
      });
    } catch (error) {
      console.error(error);
      this.setLoading(false);
    }
  };

  create = async (request: CreateUser) => {
    this.setLoading(true);
    try {
      const response = await userService.create(request);
      runInAction(() => {
        this.users.set(response.id.toString(), response);
        this.loading = false;
      });
      message.success(i18n.t("SuccessfullyAdded"));
    } catch (error) {
      console.error(error);
      this.setLoading(false);
    }
  };

  updateProfile = async (request: UpdateUser) => {
    this.setLoading(true);
    try {
      const response = await userService.updateProfile(request);
      runInAction(() => {
        this.users.set(response.id.toString(), response);
        this.loading = false;
      });
      message.success(i18n.t("SuccessfullyChanged"));
    } catch (error) {
      console.error(error);
      this.setLoading(false);
    }
  };

  createFirebaseToken = async (request: CreateFirebaseToken) => {
    try {
      await userService.createFirebaseToken(request);
    } catch (error) {
      console.error(error);
    }
  };

  setLoading = (value: boolean) => {
    runInAction(() => {
      this.loading = value;
    });
  };

  setServicesLoading = (value: boolean) => {
    runInAction(() => {
      this.servicesLoading = value;
    });
  };

  clearUsers = () => {
    runInAction(() => {
      this.users.clear();
    });
  };

  clearServices = () => {
    runInAction(() => {
      this.services.clear();
      this.servicesScopeKey = undefined;
    });
  };

  resetUser = () => {
    runInAction(() => {
      this.user = undefined;
    });
  };
}
